import { useMutation } from "@apollo/client";
import { Flex } from "@chakra-ui/react";
import { FragmentType, getFragmentData, gql } from "__generated__/gql";
import AutoTooltipIcon from "components/Shared/AutoTooltipIcon";
import LabeledToggle from "components/Shared/LabeledToggle";
import useAsyncCompleteToast from "components/Shared/useAsyncCompleteToast";
import { ADMIN_CONTEXT } from "utils/apollo/hooks/admin";
import { getActiveBrandIdContext } from "utils/apollo/hooks/brand";
import { assertPresence } from "utils/assertions";
import { useCurrentBrandId, useCurrentUserData } from "utils/currentUserHooks";

const fragment = gql(/* GraphQL */ `
  fragment SelectedEmailMarkCompletion on ReceivedEmailMessage {
    emailMessageId
    receivedEmailMessageCompletion {
      receivedEmailMessageId
      isAutomatic
    }
  }
`);

const mutation = gql(/* GraphQL */ `
  mutation ToggleEmailCompletion(
    $emailId: UUID!
    $userId: UUID!
    $isRemove: Boolean!
  ) {
    createReceivedEmailMessageCompletion(
      input: {
        receivedEmailMessageCompletion: {
          receivedEmailMessageId: $emailId
          isAutomatic: false
          userId: $userId
        }
      }
    ) @skip(if: $isRemove) {
      receivedEmailMessageCompletion {
        receivedEmailMessageId
        receivedEmailMessage {
          emailMessageId
          ...SelectedEmailMarkCompletion
        }
      }
    }
    deleteReceivedEmailMessageCompletion(
      input: { receivedEmailMessageId: $emailId }
    ) @include(if: $isRemove) {
      receivedEmailMessageCompletion {
        receivedEmailMessageId
        receivedEmailMessage {
          emailMessageId
          ...SelectedEmailMarkCompletion
        }
      }
    }
  }
`);

type Props = {
  data: FragmentType<typeof fragment> | null | undefined;
  isAdmin?: boolean;
};

export default function MarkComplete({ data, isAdmin }: Props) {
  const rem = getFragmentData(fragment, data);
  const user = useCurrentUserData();
  const completion = rem?.receivedEmailMessageCompletion;
  const isCompleted = !!completion?.receivedEmailMessageId;
  const brandId = useCurrentBrandId();

  const { showSuccessToast, showErrorToast } = useAsyncCompleteToast();
  const [performMutation, { loading }] = useMutation(mutation, {
    context: (() => {
      if (isAdmin) return ADMIN_CONTEXT;
      if (brandId) return getActiveBrandIdContext(brandId);
    })(),
  });

  const handleToggle = async (value: boolean) => {
    assertPresence(
      rem?.emailMessageId,
      "Expected email message ID to be present.",
    );
    assertPresence(user?.id, "Expected user ID to be present.");
    await performMutation({
      variables: {
        emailId: rem.emailMessageId,
        userId: user.id,
        isRemove: !value,
      },
    });
    showSuccessToast(`Email marked as ${value ? "complete" : "incomplete"}.`);
  };

  return (
    <Flex gap={2}>
      <LabeledToggle
        label="Mark as complete?"
        isOn={isCompleted}
        isDisabled={!rem?.emailMessageId || loading}
        onIsOnChange={(value) => {
          handleToggle(value).catch(showErrorToast);
        }}
        labelProps={{ fontWeight: 350, fontSize: "1em", color: "gray.800" }}
        size="md"
        width="auto"
      />
      {completion?.isAutomatic && (
        <AutoTooltipIcon label="Email was automatically marked as complete." />
      )}
    </Flex>
  );
}
